/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AclModel, L7VirtualServiceModel } from '../../models';
import { ActivatedRoute } from '@angular/router';
import { ChangeDetectorRef, Component, OnInit, ViewChildren } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from 'src/app/core/models';
import { FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { LanguageService, LogService, ModalService, UtilsService } from '../../../../../core/services';
import { Layer4ApiService, Layer7ApiService } from '../../http';
import { LbErrorService, WizardControllerStateService, WizardService, WizardTemplateService } from '../../services';
import { ServiceBaseComponent } from '../serviceComponents';
import { WizardSubStepTitlesEnum, WizardTitleEnum } from '../../enums';
import { forkJoin } from 'rxjs';
import { ipValidator, portRangeValidation } from '../../../../../core/directives';
import { pathEndValidation, pathValidation } from '../../directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-acls-2',
    templateUrl: './acls.component.html',
    styleUrls: ['./acls.component.scss']
})

export class AclsComponent extends ServiceBaseComponent implements OnInit {
    /**
     * Store type attribute for specific input.
     */
    @ViewChildren('conditionTypeAttributes')
    public conditionTypeAttributes: any;

    /**
     * Store the selected frontend mode and if it has a default acl rule.
     */
    public selectedFrontend: any = {
        id: '',
        mode: '',
        hasDefaultAcl: false
    };

    /**
     * Dropdown object containing all the dropdown values.
     */
    public dropdown = {
        frontends: [],
        conditionType: [],
        condition: [],
        redirectType: [],
        redirectLocation: [],
        redirectCode: []
    };

    /**
     * Boolean to display backends dropdown set by redirect type.
     */
    public showBackendsOption: boolean = true;

    /**
     * Boolean to display redirect text input dropdown set by redirect type.
     */
    public showRedirectText: boolean = true;

    /**
     * Store the available backends.
     */
    public availableBackends: any[] = [];

    /**
     * Flag to show/hide the redirect field.
     */
    public showRedirectField: boolean = false;

    /**
     * Store hidden content for dropdown.
     */
    public dropdownHiddenContent: any[] = [];

    /**
     * Store raw l4/l7 frontends and backends data.
     * @private
     */
    public frontends: any[] = [];
    public backends: any[] = [];

    /**
     * Store service type.
     */
    public serviceType: 'layer4' | 'layer7';

    /**
     * Store acls.
     * @private
     */
    private acls: AclModel[] = [];

    /**
     * Frontend and Backends to set as defaults.
     * @private
     */
    private _preSelectedServices: any = {};

    /**
     * Store the path type for use in showing and hidden form elements.
     * @private
     */
    private conditionTypeState: string = null;

    /**
     * Store the initial form values to compare with future changes.
     * @private
     */
    private initialData: any;

    /**
     * Flag for adding a backend.
     * @private
     */
    private isBackendAcl: boolean = false;

    /**
     * Store general acl error.
     * @private
     */
    private aclError: any;

    /**
     * Store the values of a default ACL.
     * @private
     */
    private defaultValues: any;

    constructor(
        private layer4ApiService: Layer4ApiService,
        private layer7ApiService: Layer7ApiService,
        private wizardControllerStateService: WizardControllerStateService,
        cdr: ChangeDetectorRef,
        formBuilder: FormBuilder,
        languageService: LanguageService,
        lbErrorService: LbErrorService,
        logService: LogService,
        modalService: ModalService,
        route: ActivatedRoute,
        utilsService: UtilsService,
        wizardService: WizardService,
        wizardTemplateService: WizardTemplateService
    ) {
        super(
            cdr,
            formBuilder,
            languageService,
            lbErrorService,
            logService,
            modalService,
            route,
            utilsService,
            wizardService,
            wizardTemplateService
        );

        this.setupBaseSubscriber();
        this.componentTitle = WizardTitleEnum.ACLS;
    }

    /**
     * Get form formPaths.
     */
    get conditionValues(): FormArray {
        return this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.conditionValues') as FormArray;
    }

    ngOnInit(): void {
        this.setupFormGroup();
        this.setupFormSubscribers();
        this.setupWizardSubscribers();
        this.initLoading();
    }

    /**
     * Convert control to type FormGroup.
     * @param control
     */
    public getFormGroup(control: any): FormGroup {
        return this.serviceForm.get(control) as FormGroup;
    }

    /**
     * Get a value from our form group.
     * @param controlName
     */
    public getFormValueByControlName(controlName: string): any {
        return this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + `.${controlName}`).value;
    }

    /**
     * Get the selected key value for the dropdown.
     */
    public getKeyFromTemplate(controlName: string): any {
        const template: any = this.wizardService.controllerState[WizardTitleEnum.ACLS][this.templateIndex];
        if (template && template[controlName]) {
            return template[controlName].value;
        } else {
            return undefined;
        }
    }

    /**
     * Returns true/false on whether drop is selected as the redirect type.
     */
    public isRedirectTypeDrop(): boolean {
        return this.getServiceFormField('redirectType').value === 'drop';
    }

    /**
     * Returns true/false on whether deny is selected as the redirect type.
     */
    public isRedirectTypeDeny(): boolean {
        return this.getServiceFormField('redirectType').value === 'deny';
    }

    /**
     * Check to see if default path type has been selected.
     * @return: boolean - conditionType is default
     */
    public isConditionTypeDefault(): boolean {
        return this.getFormValueByControlName('conditionType') === 'default';
    }

    /**
     * Test whether to return show the path type dropdown.
     */
    public showConditionType(): boolean {
        const template: any = this.wizardService.controllerState[WizardTitleEnum.ACLS][this.templateIndex];

        return this.frontends?.length === 1
            || template?.serviceId
            && this.getServiceFormField('serviceId').value !== null;
    }

    /**
     * Test whether to return show the condition dropdown.
     */
    public showCondition(): boolean {
        return (this.getFormValueByControlName('conditionType')
                || this.getKeyFromTemplate('condition')
                || (this.getFormValueByControlName('conditionValues')
                    && this.getFormValueByControlName('conditionValues')[0]?.length))
            && !this.isConditionTypeDefault();
    }

    /**
     * Returns true if the redirect type dropdown should show.
     */
    public showRedirectType(): boolean {
        return this.isBackendAcl
            || this.getServiceFormField('conditionType').value === null
            || this.getFormValueByControlName('condition') === null;
    }

    /**
     * Test whether to return show the redirect location.
     */
    public showRedirectLocation(): boolean {
        return this.getFormValueByControlName('redirectLocation1') && this.getFormValueByControlName('redirectLocation2');
    }

    /**
     * Show redirect location input field.
     */
    public showRedirectInput(): void {
        const redirectType: string = this.getFormValueByControlName('redirectType');

        this.showRedirectField = redirectType !== 'drop'
            && redirectType !== 'backend'
            && redirectType !== 'deny'
            && redirectType !== null;
    }

    /**
     * Determines the visibility of the backend's dropdown.
     */
    public showBackendsDropdown(): void {
        const redirectType = this.getFormValueByControlName('redirectType');
        const showBackendDropdown: boolean = redirectType === 'backend'
            && (redirectType !== 'drop'
                || redirectType !== 'urlLoc'
                || redirectType !== 'urlPre');
        // If it should show enable it.
        this.toggleInputVisibility('redirectLocation1',
            `${showBackendDropdown ? 'enable' : 'disable'}`);
        this.showBackendsOption = showBackendDropdown;
    }

    /**
     * Test whether to return show the redirect location input field.
     * @private
     */
    private showRedirectTextInput(): void {
        const redirectType: string = this.getFormValueByControlName('redirectType');
        const showRedirectText: boolean = redirectType !== 'drop'
            && this.getServiceFormField('conditionValues').valid
            && this.getServiceFormField('conditionType').valid
            && redirectType !== 'backend'
            && !this.isConditionTypeDefault();
        // If it should show enable it.
        if (showRedirectText) {
            this.getServiceFormField('redirectLocation2').enable();
        } else {
            this.getServiceFormField('redirectLocation2').disable();
        }
        this.showRedirectText = showRedirectText;
    }

    /**
     * If the condition type is header or query then show additional text input.
     */
    public showConditionParam(): boolean {
        return this.isConditionType('query') || this.isConditionType('header');
    }

    /**
     * Helper function to show/hide redirect section.
     */
    public hideRedirectSection(): boolean {
        return !this.getFormValueByControlName('redirectType')
            && !this.showRedirectLocation();
    }

    /**
     * Show the add button for additional input fields.
     */
    public showAddValueButton(): boolean {
        return !this.isConditionType('query') && !this.isConditionType('header');
    }

    /**
     * Stop user from adding another path is the path is not valid.
     */
    public canAddValue(): boolean {
        return this.conditionValues.value[0] !== '' && this.getServiceFormField('conditionValues').valid;
    }

    /**
     * Add a path to the paths FormArray.
     */
    public addValueFilter(): void {
        if (this.canAddValue()) {
            const conditionType: string = this.getFormValueByControlName('conditionType');
            this.conditionValues.push(new FormControl('', Validators.required));
            this.cdr.detectChanges();
            this.setInputAttributes(conditionType);
        }
    }

    /**
     * Delete a condition value field from FormArray by index.
     * @param i
     */
    public removeConditionValue(i: number): void {
        this.conditionValues.removeAt(i);
    }

    /**
     * Set the progress line point depending on path type.
     * @param type
     */
    public setProgressLine(type: string): any {
        if (this.getFormValueByControlName('condition') !== null) {
            return this.getFormValueByControlName(type);
        }
    }

    /**
     * Check for duplicate path values.
     */
    public checkForDuplicateValues(): void {
        let duplicateControls: any[] = [];
        const sortedArr: any[] = this.conditionValues.value.slice().sort();

        for (let i = 0; i < sortedArr.length - 1; i++) {
            if (sortedArr[i + 1] === sortedArr[i]) {
                for (const control of this.conditionValues.controls) {
                    if (control.value === sortedArr[i]) {
                        duplicateControls.push(control);
                    }
                }

                if (duplicateControls.length >= 1) {
                    duplicateControls[duplicateControls.length - 1].setErrors({
                        'duplicateValidator': `${this.languageService.translate('duplicate_value')}`
                    });
                }
                duplicateControls = [];
            }
        }

        this.outputFormValues();
    }

    /**
     * Check if the selected service has SSL certificates.
     */
    public hasSSLCerts(): boolean {
        const serviceId: string = this.getServiceFormField('serviceId').value;
        const frontend: any = this.frontends.find(item => item.id === serviceId);

        return frontend?.sslCerts?.length !== 0;
    }

    /**
     * Check for condition type of header or query.
     * @param name
     * @private
     */
    private isConditionType(name: string): boolean {
        return this.getFormValueByControlName('conditionType') === name;
    }

    /**
     * Setup dropdown arrays object.
     * @private
     */
    private setUpDropdownContent(): void {
        this.dropdown.frontends = this.getDropDownFrontends();
        this.dropdown.conditionType = this.getDropdownConditionTypes();
        this.dropdown.condition = this.getDropdownCondition();
        this.dropdown.redirectType = this.getDropdownRedirectTypes();
        this.dropdown.redirectLocation = this.getDropdownBackends();
        this.dropdown.redirectCode = this.getDropdownRedirectCode();
    }

    /**
     * Get the dropdown value for the bool equal or not equal to the path.
     * @private
     */
    private getDropdownCondition(): DropdownModel[] {
        return [
            <DropdownModel>{ key: 'eq', value: 'Equals' },
            <DropdownModel>{ key: 'ne', value: 'Not Equals' }
        ];
    }

    /**
     * Update the dropdown conditions based on the condition type.
     * @param conditionType
     * @private
     */
    private updateDropdownConditions(conditionType: string): void {
        let dropdownConditions: any[] = [];
        if (conditionType === 'nbsrv') {
            dropdownConditions.push(
                <DropdownModel>{ key: 'gt', value: 'Greater Than' },
                <DropdownModel>{ key: 'lt', value: 'Less Than' },
                <DropdownModel>{ key: 'gt', value: 'Greater Than or Equal' },
                <DropdownModel>{ key: 'lt', value: 'Less Than or Equal' }
            );
        } else {
            dropdownConditions = this.getDropdownCondition();
        }

        this.dropdown.condition = dropdownConditions;
    }

    /**
     * Get the dropdown value for redirect type.
     * @private
     */
    private getDropdownRedirectTypes(): DropdownModel[] {
        const dropdownList: DropdownModel[] = [
            <DropdownModel>{ key: 'drop', value: 'Drop' },
            <DropdownModel>{ key: 'deny', value: 'Deny' },
            <DropdownModel>{ key: 'backend', value: 'Use Backend' },
        ];

        if (this.selectedFrontend.mode === 'http') {
            dropdownList.push(
                <DropdownModel>{ key: 'urlLoc', value: 'URL location' },
                <DropdownModel>{ key: 'urlPre', value: 'URL prefix' },
                <DropdownModel>{ key: 'https', value: 'HTTPS' },
            );
        }

        if (this.isConditionTypeDefault()) {
            dropdownList.push(
                <DropdownModel>{ key: 'backend', value: 'Use Backend' },
            );
        }

        return dropdownList;
    }

    /**
     * Update redirect type dropdown.
     * @param conditionType
     * @private
     */
    private updateRedirectTypes(conditionType: string): void {
        let redirectTypes: any[];
        if (conditionType === 'default' || this.redirectTypeIsBackend()) {
            redirectTypes = [<DropdownModel>{ key: 'backend', value: 'Use Backend' }];
        } else {
            redirectTypes = this.getDropdownRedirectTypes();
        }

        this.dropdown.redirectType = redirectTypes;
    }

    /**
     * Check if the redirect type is backend
     * @private
     */
    private redirectTypeIsBackend(): boolean {
        return this.getFormValueByControlName('redirectType') === 'backend';
    }

    /**
     * Get the dropdown value for the path types.
     * @private
     */
    private getDropdownConditionTypes(hideDefault?: boolean): DropdownModel[] {
        const templateType: string = this.wizardService.selectedTemplate.type;
        if (templateType === 'layer4') {
            return [<DropdownModel>{ key: 'default', value: 'Default' }];
        }

        const dropdownList: DropdownModel[] = [
            <DropdownModel>{ key: 'srcBlk', value: 'IP Address' },
            <DropdownModel>{ key: 'dstPort', value: 'Port' },
            <DropdownModel>{ key: 'default', value: 'Default' }
        ];

        if (hideDefault) {
            if (!this.dropdownHiddenContent.includes('default')) {
                this.dropdownHiddenContent.push('default');
                this.dropdownHiddenContent = [...this.dropdownHiddenContent];
            }
        }

        if (this.selectedFrontend.mode === 'http') {
            dropdownList.push(
                <DropdownModel>{ key: 'pathBeg', value: 'path_beg' },
                <DropdownModel>{ key: 'pathEnd', value: 'path_end' },
                <DropdownModel>{ key: 'hdrHost', value: 'hdr_host' },
                <DropdownModel>{ key: 'hdrBeg', value: 'hdr_beg(Host)' },
                <DropdownModel>{ key: 'query', value: 'Query' },
                <DropdownModel>{ key: 'path', value: 'path' },
                <DropdownModel>{ key: 'header', value: 'hdr' },
                <DropdownModel>{ key: 'flag', value: 'Flags' },
            );
        }

        if (this.selectedFrontend.mode === 'tcp' && this.hasSSLCerts()) {
            dropdownList.push(<DropdownModel>{ key: 'sslSNI', value: 'ssl_sni' });
        }

        return dropdownList;
    }

    /**
     * Get the dropdown value for the bool equal or not equal to the path.
     * @private
     */
    private getDropdownRedirectCode(): DropdownModel[] {
        return [
            <DropdownModel>{ key: 200, value: '200 OK' },
            <DropdownModel>{ key: 400, value: '400 Bad Request' },
            <DropdownModel>{ key: 401, value: '401 Unauthorized' },
            <DropdownModel>{ key: 403, value: '403 Forbidden' },
            <DropdownModel>{ key: 404, value: '404 Not Found' },
            <DropdownModel>{ key: 405, value: '405 Method Not Allowed' },
            <DropdownModel>{ key: 407, value: '407 Proxy Authentication Required' },
            <DropdownModel>{ key: 408, value: '408 Request Timeout' },
            <DropdownModel>{ key: 410, value: '410 Gone' },
            <DropdownModel>{ key: 425, value: '425 Too Early' },
            <DropdownModel>{ key: 429, value: '429 Too Many Requests' },
            <DropdownModel>{ key: 500, value: '500 Internal Server Error' },
            <DropdownModel>{ key: 502, value: '502 Bad Gateway' },
            <DropdownModel>{ key: 503, value: '503 Service Unavailable' },
            <DropdownModel>{ key: 504, value: '504 Gateway Timeout' },
        ];
    }

    /**
     * Reset the paths to a single empty array item.
     * We are using this method, so we don't lose subscriptions to the inputs.
     * @private
     */
    private resetTextInputFilter(): void {
        // If there are multiple paths remove them all but the first one
        if (this.conditionValues.length > 1) {
            while (this.conditionValues.length > 1) {
                this.conditionValues.removeAt(0);
            }
        }
        // Set the first one to an empty string.
        this.conditionValues.controls[0].setValue('');
    }

    /**
     * Sets selected l7Services as the only option in the dropdowns to force user to these selections.
     * @private
     */
    private setSelectedServices(): void {
        if (this._preSelectedServices) {
            const { frontend, backend } = this._preSelectedServices;
            if (frontend && !this.frontends.find(item => item.id === frontend.id)) {
                this.frontends = [frontend];
                this.dropdown.frontends = this.getDropDownFrontends();
                this.setInputValue('serviceId', frontend.id);
            }

            if (backend && !this.backends.find(item => item.id === backend.id)) {
                this.dropdown.redirectType = [<DropdownModel>{ key: 'backend', value: 'Use Backend' }];
                backend.id = backend.id.length ? backend.id : '$1';
                this.backends = [backend];
                this.dropdown.redirectLocation = this.getDropdownBackends();
            }
        }
    }

    /**
     * Fetch data required for the wizard.
     * @private
     */
    private initLoading(): void {
        this.serviceType = this.route.snapshot.params['serviceType'];
        const apiService: any = this.serviceType === 'layer4' ? this.layer4ApiService : this.layer7ApiService;
        const apiCalls: any = {
            'frontends': apiService.getFrontends(),
            'backends': apiService.getBackends(),
            'acls': apiService.getAclRules(),
        };
        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: any): void => {
                    const serviceError: any = this.getErrorForService(WizardTitleEnum.ACLS);
                    const template: any = this.getTemplate(WizardTitleEnum.ACLS, serviceError);

                    this.frontends = dataGroup['frontends'];
                    this.backends = dataGroup['backends'];
                    this.acls = dataGroup['acls'];
                    this.patchTemplateToForm(template);
                    this.initialData = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL).value;
                    // Once everything is loaded check to see if there are any error in the controller state
                    // NOTE: There is a race condition in the way the forms are built, this enables the error to be displayed.
                    const timerId = setTimeout(() => {
                        this.checkForErrorState();
                        clearTimeout(timerId);
                    }, 0);
                }
            });
    }

    /**
     * Initialise form values.
     * @private
     */
    private patchTemplateToForm(template: any): void {
        if (template.conditionType.value === 'default') {
            this.defaultValues = {
                serviceId: template.serviceId,
                redirectLocation: template.redirectLocation
            };
        }

        // Populate our patch
        const templatePatch: any = this.createTemplatePatch(template);
        const id: string = template?.id?.value;
        const editMode: boolean = this.utilsService.isServiceId(id) || false;

        if (editMode || (id?.includes('temp-') && template.redirectType.value)) {
            const controllerState: any = this.wizardService.controllerState;
            this.applyChangesFromTemplatePatch(templatePatch);
            const frontends: any = controllerState[WizardTitleEnum.FRONTENDS];
            const backends: any = controllerState[WizardTitleEnum.BACKENDS];
            if (backends?.length || frontends?.length) {
                this.setPreSelectedServices(controllerState, frontends, backends);
                this.setSelectedServices();
            }
        }

        // Check if we are in advanced mode and set the correct states on the inputs before patching
        this.advancedMode = this.wizardService.wizardMode;

        // Patch our hidden state
        this.patchTemplateHiddenState(template, this.inputStateStore);
        this.registerSubSteps(WizardTitleEnum.ACLS);

        this.applyPatchAndValidate(templatePatch);

        // Tell the wizard to show the content
        this.wizardService.isLoading = false;

        // Once patched output the values to the controller state.
        this.outputFormValues();

        // Dropdown content setup here as the content depends on pre-selected services.
        this.setUpDropdownContent();

        this.checkFrontendForDefault();

        if (template?.serviceId.value && template?.serviceId.value !== '$1' && template.id.value === null) {
            this.isBackendAcl = true;
            this.updateConditionsAndTypesForBackend();
        }
    }

    /**
     * Set the pre-selected services object.
     * @param controllerState
     * @param frontends
     * @param backends
     * @private
     */
    private setPreSelectedServices(controllerState: any, frontends: any[], backends: any[]): void {
        this._preSelectedServices = {};
        if (frontends?.length) {
            const templateIndex: number = frontends?.length === backends?.length ? this.templateIndex : 0;
            this._preSelectedServices['frontend'] = this.wizardControllerStateService.getValuesFromServiceData(
                controllerState, frontends[templateIndex]
            );
        }

        if (backends?.length) {
            this._preSelectedServices['backend'] = this.wizardControllerStateService.getValuesFromServiceData(
                controllerState, backends[this.templateIndex]
            );
        }
    }

    /**
     * Check the selected frontend for a default ACL rule.
     * @private
     */
    private checkFrontendForDefault(): void {
        const frontendId: string = this.getServiceFormField('serviceId').value;
        const apiService: any = this.wizardService.selectedTemplate.type === 'layer4' ? this.layer4ApiService : this.layer7ApiService;
        if (frontendId) {
            apiService.getFrontendAcls(frontendId)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: AclModel[]) => {
                        if (data?.length) {
                            const defaultAcl: AclModel = data.find(item => item.conditionType === 'default');
                            if (defaultAcl) {
                                this.dropdown.conditionType = this.getDropdownConditionTypes(true);
                            }
                        }
                    },
                    error: (error: ErrorResponseModel) => {
                        this.logService.error('AclsComponent: getFrontendAcls', 'Error fetching acls', error);
                    }
                });
        }
    }

    /**
     * Apply changes to the view based on template patch values.
     * @param templatePatch
     * @private
     */
    private applyChangesFromTemplatePatch(templatePatch: any): void {
        if (templatePatch['serviceId']) {
            this.setFrontendDetails(templatePatch['serviceId']);
        }

        if (templatePatch['conditionValues']?.length) {
            // Create controllers for each path before patching the values.
            this.createConditionFormControls(templatePatch['conditionValues'].length);
        }

        if (templatePatch['redirectType'] === 'deny') {
            templatePatch['redirectLocation'] = null;
        }

        if (templatePatch['conditionType']) {
            this.conditionTypeState = templatePatch['conditionType'];
        }

        if (templatePatch['redirectLocation']) {
            const input = templatePatch['redirectType'] === 'backend' ? 'redirectLocation1' : 'redirectLocation2';
            this.toggleInputVisibility(input, 'enable');
            this.setInputValue(input, templatePatch['redirectLocation']);
        }
    }

    /**
     * Update the conditions and types for adding a backend acl.
     * @private
     */
    private updateConditionsAndTypesForBackend(): void {
        this.dropdown.frontends = this.getDropDownFrontends();
        this.dropdown.conditionType = this.getDropdownConditionTypes();
        this.dropdown.condition = this.getDropdownCondition();
        this.dropdown.redirectType = [<DropdownModel>{ key: 'backend', value: 'Use Backend' },];
    }

    /**
     * Check for changes in the form to determine the submit button disabled state.
     * @param formValue
     * @private
     */
    private checkForFormChanges(formValue: any): void {
        let formValid: boolean;
        formValid = this.serviceForm.valid;

        if (this.wizardService.wizardSteps.length === 1 && this.initialData) {
            formValid = formValid && this.utilsService.hasFormDataChanged(this.initialData, formValue);
        }

        this.wizardService.isSectionValidEmitter.next(formValid);
    }

    /**
     * Return dropdown structure array for frontends.
     * @private
     */
    private getDropDownFrontends(): DropdownModel[] {
        this.dropdown.frontends = [];
        const convertedFrontends: any[] = [];
        const serviceId: string = this.wizardService.controllerState[WizardTitleEnum.ACLS][this.templateIndex]?.serviceId?.value;

        // If you are in edit mode (you have a serviceId), disable the frontend dropdown with the serviceId value set.
        if (serviceId && serviceId !== '$1') {
            const frontend: any = this.frontends.find(frontendItem => frontendItem.id === serviceId);
            this.getServiceFormField('serviceId').disable();

            return [<DropdownModel>{ key: frontend.id, value: frontend.name }];
        }

        for (const frontend of this.frontends) {
            if (this.serviceType === 'layer4') {
                if (frontend.aclIds.length === 0) {
                    convertedFrontends.push(<DropdownModel>{ key: frontend.id, value: frontend.name });
                }
            } else {
                if (this.backends?.length === 1) {
                    const backend: any = this.backends[0];
                    if (backend.mode === 'http' && frontend.mode !== 'tcp'
                        && frontend.type !== 'monitor' || backend.mode === 'tcp') {
                        convertedFrontends.push(<DropdownModel>{ key: frontend.id, value: frontend.name });
                    }
                } else {
                    if (frontend.type !== 'monitor') {
                        convertedFrontends.push(<DropdownModel>{ key: frontend.id, value: frontend.name });
                    }
                }
            }
        }

        return convertedFrontends;
    }

    /**
     * Get the dropdown value for backends.
     * @private
     */
    private getDropdownBackends(): DropdownModel[] {
        let convertedBackends: DropdownModel[] = [];
        const selectedAcls = [];
        const preSelectedBackend = this._preSelectedServices['backend'];
        const template = this.wizardService.controllerState[WizardTitleEnum.ACLS][this.templateIndex];

        for (const backend of this.backends) {
            if (this.serviceType === 'layer4') {
                convertedBackends.push(<DropdownModel>{ key: backend.id, value: backend.name });
            }
            if (
                // Include backend if it's in the template
                (this.utilsService.getObjectValue(template, 'redirectLocation.value') === backend.id
                    // or the backend is not already in use with the selected frontend.
                    || !selectedAcls.some(acl => acl.redirectLocation === backend.id))
                // And the mode matches is correct for the frontend.
                && ((this.selectedFrontend.mode === 'http' && backend.mode !== 'tcp') || this.selectedFrontend.mode === 'tcp')
            ) {
                convertedBackends.push(<DropdownModel>{ key: backend.id, value: backend.name });
            }

            if ((this.selectedFrontend?.id === '') && (this.backends?.length === 1)) {
                convertedBackends.push(<DropdownModel>{ key: backend.id, value: backend.name });
            }

            if (preSelectedBackend) {
                if (backend.id === preSelectedBackend.id) {
                    convertedBackends = [];
                    convertedBackends.push(<DropdownModel>{ key: backend.id, value: backend.name });
                }
            }
        }

        if (preSelectedBackend && !this.backends.find(item => item.id === preSelectedBackend.id)) {
            convertedBackends = [];
            convertedBackends.push(<DropdownModel>{ key: preSelectedBackend.id, value: preSelectedBackend.name });
        }

        if (convertedBackends.length === 1) {
            // If the is only one backend, set its value in the form
            this.setInputValue('redirectLocation1', convertedBackends[0].key);
            this.toggleInputVisibility('redirectLocation1', 'disable');
        }

        return convertedBackends;
    }

    /**
     * Creates a form controller for each path in the template in edit mode.
     * @param total
     * @private
     */
    private createConditionFormControls(total: number): void {
        for (let i = 1; i < total; i++) {
            this.conditionValues.push(new FormControl(null, Validators.required));
        }
    }

    /**
     * Rerun form checks after a value has been updated in code.
     * @private
     */
    private checkFormAfterValueReset(): void {
        this.serviceForm.markAsPristine();
        this.serviceForm.markAsUntouched();
        this.serviceForm.updateValueAndValidity();
    }

    /**
     * Setup form subscribers for listening to form events.
     * @private
     */
    private setupFormSubscribers(): void {
        // Set up our form value changes subscriber
        this.serviceForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: () => {
                    this.utilsService.debounce('aclFormChanges', () => {
                        this.checkFormForErrors();
                        if (this.isCurrentStepValid()) {
                            this.outputFormValues();
                        }
                    });
                }
            });

        // Set up value change listeners for each field that is required or needs validating.
        this.getServiceFormField('serviceId').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (serviceId: string): void => {
                    const template: any = this.wizardService.controllerState[WizardTitleEnum.ACLS][this.templateIndex];
                    this.setFrontendDetails(serviceId);

                    if (template['serviceId'].value === '$1') {
                        this.updateFormValidation(serviceId);
                        this.dropdown.redirectType = this.getDropdownRedirectTypes();
                    }

                    if (this.hasDefaultAcl(serviceId)) {
                        this.dropdown.conditionType = this.getDropdownConditionTypes(true);
                    }
                }
            });

        this.getServiceFormField('conditionType').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (conditionType: string) => {
                    if (conditionType) {
                        this.applyDefaultValues(conditionType);
                        this.updateRedirectTypes(conditionType);
                        this.setInputAttributes(conditionType);
                        this.checkConditionTypeState(conditionType);
                        this.updateDropdownConditions(conditionType);
                        this.enableDisableConditionValuesInput();
                        this.updateVisibility(conditionType);
                    }
                }
            });

        this.getServiceFormField('condition').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (condition: any) => {
                    if (condition !== null) {
                        this.toggleInputVisibility('conditionValues', 'enable');
                    }
                }
            });

        this.getServiceFormField('conditionValues').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: () => {
                    if (this.getServiceFormField('conditionValues').enabled) {
                        if (this.getServiceFormField('conditionValues').valid) {
                            this.toggleInputVisibility('redirectType', 'enable');
                        } else {
                            this.toggleMultipleInputs(['redirectType', 'redirectLocation2'], 'disable');
                        }
                    }
                }
            });

        this.getServiceFormField('redirectType').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (redirectType: string) => {
                    this.checkFormAfterValueReset();
                    this.showBackendsDropdown();
                    this.showRedirectTextInput();
                    this.setFormFieldVisibility(redirectType);
                    this.showRedirectInput();
                }
            });

        this.getServiceFormField('redirectLocation2').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (redirectLocation: string) => {
                    if (redirectLocation === '$1') {
                        this.getServiceFormField('redirectLocation2')
                            .setErrors({ 'invalidLocation': `${this.languageService.translate('invalid_redirect_location')}` });
                    }
                }
            });
    }

    /**
     * Check if selected frontend has a default ACL attached to it.
     * @param serviceId
     * @private
     */
    private hasDefaultAcl(serviceId: string): boolean {
        let matchingACL: boolean;
        const frontend: any = this.frontends.find(item => item.id === serviceId);
        if (frontend) {
            matchingACL = frontend.aclIds.some((item: string) => {
                return this.acls.find(rule => rule.id === item && rule.conditionType === 'default');
            });
        }

        return matchingACL;
    }

    /**
     * Re-apply the default acl if in edit mode.
     * @param conditionType
     * @private
     */
    private applyDefaultValues(conditionType: string): void {
        if (conditionType !== 'default') {
            return;
        }
        this.toggleInputVisibility('redirectLocation1', 'enable');
        this.setInputValue('redirectLocation1', this.defaultValues?.redirectLocation.value);
    }

    /**
     * Enable/Disable form fields based on redirectType.
     * @param redirectType
     * @private
     */
    private setFormFieldVisibility(redirectType: string): void {
        if (redirectType === 'deny') {
            this.toggleInputVisibility('redirectCode', 'enable');
            this.toggleMultipleInputs(['redirectLocation1', 'redirectLocation2', 'conditionParam'], 'disable');
        } else {
            this.toggleInputVisibility('redirectCode', 'disable');
        }

        if (redirectType === 'drop') {
            this.toggleMultipleInputs(['redirectLocation1', 'redirectLocation2', 'conditionParam'], 'disable');
        }
    }

    /**
     * Enable/Disable the appropriate input fields depending on the path type selection.
     * @param conditionType
     * @private
     */
    private updateVisibility(conditionType: string): void {
        let optionsArray: string[] = [];
        switch (conditionType) {
            case 'dstPort':
            case 'flag':
            case 'pathBeg':
            case 'pathEnd':
            case 'hdrHost':
            case 'hdrBeg':
            case 'srcBlk':
                this.toggleMultipleInputs(['condition', 'conditionValues'], 'enable');
                this.toggleInputVisibility('conditionParam', 'disable');
                break;
            case 'query':
            case 'header':
                optionsArray = ['condition', 'conditionValues', 'conditionParam'];
                this.toggleMultipleInputs(optionsArray, 'enable');
                break;
            case 'default':
                this.toggleInputVisibility('condition', 'disable');
                this.toggleInputVisibility('redirectType', 'enable');
                this.toggleInputVisibility('conditionParam', 'disable');
                this.setInputValue('redirectType', 'backend');
                this.setControlValidators(null, true);
                break;
        }
    }

    /**
     * Set the input's attributes depending on its path type.
     * @param conditionType
     * @private
     */
    private setInputAttributes(conditionType: string): void {
        const inputAttributes: any = this.getInputAttributes(conditionType);
        this.conditionTypeAttributes.map((item: any) => {
            // Port is the only class we add, so we should remove it before looping the attributes.
            item.nativeElement.classList.remove('port');
            for (const attribute in inputAttributes) {
                if (inputAttributes.hasOwnProperty(attribute)) {
                    if (attribute === 'class') {
                        item.nativeElement.classList.add(inputAttributes[attribute]);
                    } else {
                        item.nativeElement[attribute] = inputAttributes[attribute];
                    }
                }
            }
        });
    }

    /**
     * Get the attributes of the different inputs.
     * @param conditionType
     * @private
     */
    private getInputAttributes(conditionType: string): any {
        const attributes: any = {};
        attributes['type'] = 'text';
        switch (conditionType) {
            case 'dstPort':
                attributes['placeholder'] = '8080';
                attributes['type'] = 'number';
                attributes['min'] = 0;
                attributes['max'] = 65535;
                attributes['class'] = 'port';
                this.setControlValidators(portRangeValidation);
                break;
            case 'flag':
                attributes['placeholder'] = 'flag_a || flag_b';
                this.setControlValidators(null, false);
                break;
            case 'hdrBeg':
                attributes['placeholder'] = 'www';
                this.setControlValidators(null, false);
                break;
            case 'hdrHost':
                attributes['placeholder'] = 'example.com';
                this.setControlValidators(null, false);
                break;
            case 'header':
                attributes['placeholder'] = 'application/json';
                this.setControlValidators(null, false);
                break;
            case 'path':
                attributes['placeholder'] = '/';
                this.setControlValidators(null, false);
                break;
            case 'pathBeg':
                attributes['placeholder'] = '/example';
                this.setControlValidators(pathValidation);
                break;
            case 'pathEnd':
                attributes['placeholder'] = '.png';
                this.setControlValidators(pathEndValidation);
                break;
            case 'srcBlk':
                attributes['placeholder'] = '10.1.0.0/16';
                this.setControlValidators(ipValidator);
                break;
            case 'sslSNI':
                attributes['placeholder'] = 'www.example.com';
                this.setControlValidators(null, false);
                break;
            case 'query':
                attributes['placeholder'] = 'URL/Text';
                this.setControlValidators(null, false);
                break;
        }

        return attributes;
    }

    /**
     * Set the selected frontend state, stores both 'mode' and 'has Default' ACL at class level.
     * @param frontendId
     * @private
     */
    private setFrontendDetails(frontendId: string): void {
        if (frontendId && this.selectedFrontend.id !== frontendId) {
            // Empty all values that might be associated to the previous frontend.
            this.resetFormByOption('serviceId');
            const selectedFrontend = this.frontends.find(frontend => frontend.id === frontendId);
            const rules = this.acls.filter(item => selectedFrontend.aclIds.includes(item.id));
            const hasDefaultAcl: boolean = rules.some(item => item.conditionType === 'default');

            this.selectedFrontend = {
                id: frontendId,
                mode: selectedFrontend.mode,
                hasDefaultAcl
            };

            this.dropdown.redirectLocation = this.getDropdownBackends();
            this.dropdown.conditionType = this.getDropdownConditionTypes();
            this.updateBackendsAfterReset();
        }
    }

    /**
     * Update the backend's selection after the form has been reset.
     * @private
     */
    private updateBackendsAfterReset(): void {
        const backend: any = this.wizardService.controllerState[WizardTitleEnum.BACKENDS];
        if (backend?.length) {
            this.dropdown.redirectType = [<DropdownModel>{ key: 'backend', value: 'Backend' }];
            this.backends = backend;
            this.dropdown.redirectLocation = this.getDropdownBackends();
        }
    }

    /**
     * Check selected condition type against condition type state and reset form values if required.
     * @param conditionType
     * @private
     */
    private checkConditionTypeState(conditionType: string): void {
        if (conditionType !== this.conditionTypeState) {
            this.resetFormByOption('conditionTypeChanged');
        }
    }

    /**
     * Override the html default state disabled when condition is set.
     * @private
     */
    private enableDisableConditionValuesInput(): void {
        const condition: string = this.getServiceFormField('condition').value;
        const state: 'disable' | 'enable' = condition === null ? 'disable' : 'enable';
        for (const control of this.conditionValues.controls) {
            if (control) {
                control[state]();
                control.updateValueAndValidity();
            }
        }
    }

    /**
     * Toggle sections visibility.
     * @param section
     * @param operation
     * @private
     */
    private toggleInputVisibility(section: string, operation: string): void {
        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + `.${section}`)[operation]();
    }

    /**
     * Set the validators on the controls depending on path type or clear if type is default.
     * @param validator
     * @param isDefault
     * @private
     */
    private setControlValidators(validator?: ValidatorFn, isDefault?: boolean): void {
        for (const control of this.conditionValues.controls) {
            if (!isDefault) {
                if (validator) {
                    control.setValidators([validator, Validators.required]);
                } else {
                    control.setValidators(Validators.required);
                }
            } else {
                control.setValue('');
                control.clearValidators();
            }

            control.updateValueAndValidity();
        }
    }

    /**
     * Resets the form values based on the options changed.
     * @param option
     * @private
     */
    private resetFormByOption(option: string): void {
        if (option === 'serviceId') {
            this.setInputValue('conditionType', null);
        }

        if (option === 'serviceId' || option === 'conditionTypeChanged') {
            this.setInputValue('condition', null);
            this.resetTextInputFilter();
            this.setInputValue('redirectType', null);
            this.setInputValue('redirectLocation1', null);
            this.setInputValue('redirectLocation2', null);
        }
        this.checkFormAfterValueReset();
    }

    /**
     * Format form values for output.
     * @private
     */
    private outputFormValues(): void {
        if (!this.wizardService.isLoading) {
            const aclOj: any = this.flattenServiceFormValues(<AclModel>{});

            aclOj.redirectLocation = this.getServiceFormField('redirectLocation1').value
                ? this.getServiceFormField('redirectLocation1').value
                : this.getServiceFormField('redirectLocation2').value;

            this.checkForFormChanges(aclOj);

            const propertiesToDelete: string[] = [
                'redirectLocation1',
                'redirectLocation2',
            ];

            const redirectType: string = this.getServiceFormField('redirectType').value;

            if (redirectType === 'deny' || redirectType === 'drop') {
                if (aclOj.hasOwnProperty('redirectCode')) {
                    aclOj.redirectCode = parseInt(aclOj.redirectCode, 10);
                }

                propertiesToDelete.push('redirectLocation');
            }

            const controllerState: any = this.wizardService.controllerState;

            if (this.serviceForm.valid) {
                const aclServiceId = aclOj.serviceId ? aclOj.serviceId : this.getServiceFormField('serviceId').value;
                this.setFrontendInControllerState(controllerState, aclServiceId);
            }

            this.removeUnneededProperties(aclOj, propertiesToDelete);

            // Loop through form values
            const template: any = controllerState[WizardTitleEnum.ACLS][this.templateIndex];

            if (this.isRedirectTypeDeny()) {
                template.redirectLocation.hidden = true;
                template.redirectLocation.value = null;
            }

            for (const property in aclOj) {
                if (aclOj.hasOwnProperty(property)) {
                    const visibility = this.inputStateStore[property]?.hidden;
                    template[property] = {
                        value: aclOj[property],
                        hidden: visibility !== undefined ? visibility : true
                    };
                }
            }
        }
    }

    /**
     * Add selected frontend to the controller state.
     * @param controllerState
     * @param serviceId
     * @private
     */
    private setFrontendInControllerState(controllerState: any, serviceId: string): void {
        const frontend: L7VirtualServiceModel = this.frontends.find(item => item.id === serviceId);
        this.utilsService.createArrayIfNotExist(controllerState, WizardTitleEnum.FRONTENDS);
        controllerState[WizardTitleEnum.FRONTENDS] = [{
            id: { value: serviceId },
            aclIds: { value: frontend.aclIds.length ? frontend.aclIds : [] }
        }];
    }

    /**
     * Setup wizard subscribers.
     * @private
     */
    private setupWizardSubscribers(): void {
        /**
         * Advanced Mode Subscriber
         * Note: We subscribe to whether the advanced mode button has been toggled.
         */
        this.wizardService.wizardModeEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (advancedState: any): void => {
                    this.advancedMode = advancedState;
                }
            });

        this.wizardService.errorNotificationClosed
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (state: any): void => {
                    if (state) {
                        // Clear validation variables
                        this.clearErrorValidation();
                    }
                }
            });
    }

    /**
     * Clear the errored fields.
     * @private
     */
    private clearErrorValidation(): void {
        this.resetValidationOnErroredFields();
        this.aclError = {};
    }

    /**
     * Reset errors on errored fields.
     * @private
     */
    private resetValidationOnErroredFields(): void {
        this.clearConditionValueErrors();
        this.clearRedirectCodeErrors();
    }

    /**
     * Clear errors on condition values controls.
     * @private
     */
    private clearConditionValueErrors(): void {
        const conditionValues = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.conditionValues') as FormArray;
        if (conditionValues?.length) {
            for (const value of conditionValues.controls) {
                if (value.hasError('duplicateValue')) {
                    value.markAsUntouched();
                    value.setErrors({ 'duplicateValue': null });
                    value.updateValueAndValidity();
                }
            }
        }
    }

    /**
     * Clear error on redirect code field.
     * @private
     */
    private clearRedirectCodeErrors(): void {
        const redirectCodeError: any[] = this.aclError?.filter((item: {
            field: string;
        }) => item.field === 'redirectCode');
        if (redirectCodeError?.length) {
            const redirectCode: any = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.redirectCode');
            redirectCode.markAsUntouched();
            redirectCode.setErrors(null);
            redirectCode.updateValueAndValidity();
        }
    }

    /**
     * Change the form setup.
     * @param serviceId
     * @private
     */
    private updateFormValidation(serviceId: string): void {
        this.setFrontendDetails(serviceId);
        // Update the frontend & backends dropdowns.
        this.dropdown.frontends = this.getDropDownFrontends();
        this.dropdown.redirectLocation = this.getDropdownBackends();
    }

    /**
     * Check for errors.
     * @private
     */
    private checkForErrorState(): void {
        this.aclError = this.utilsService.getObjectValue(
            this.wizardService.controllerState,
            `errorState.${WizardTitleEnum.ACLS}`
        );

        if (this.aclError?.length) {
            this.loadedWithErrors(this.aclError);
        }
    }

    /**
     * Check form for errors.
     * @private
     */
    private checkFormForErrors(): void {
        if (this.aclError?.error?.length) {
            this.setConditionValuesErrorObject();
        }
    }

    /**
     * Set the condition values error object and push to array.
     * @private
     */
    private setConditionValuesErrorObject(): void {
        const conditionValuesErrors: any[] = this.aclError?.error?.filter((error: any) => error.field === 'conditionValues');
        const conditions: any[] = this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL)['controls'].conditionValues.controls;
        const index: number = 0;

        for (const condition of conditions) {
            const errorValues = JSON.parse(conditionValuesErrors[index].value);
            if (errorValues.includes(condition.value)) {
                condition.markAsTouched();
                condition.markAsDirty();
                condition.setErrors({ 'duplicateValue': conditionValuesErrors[index].message });
            } else {
                condition.setErrors({ 'duplicateValue': null });
                condition.updateValueAndValidity({ emitEvent: false });
            }
        }

        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.conditionValues').updateValueAndValidity({ emitEvent: false });

        // Fixes timing issue with the submit button being enabled when the form is valid.
        if (this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL).valid) {
            this.outputFormValues();
        }
    }

    /**
     * Set input value.
     * @param section
     * @param value
     * @private
     */
    private setInputValue(section: string, value: any): void {
        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.' + section).setValue(value);
    }

    /**
     * Enable/Disable groups of inputs.
     * @param optionsArray
     * @param operation
     * @private
     */
    private toggleMultipleInputs(optionsArray: string[], operation: string): void {
        if (optionsArray.length) {
            for (const option of optionsArray) {
                this.toggleInputVisibility(option, operation);
            }
        }
    }

    /**
     * Get specific form control.
     * @param field
     * @private
     */
    private getServiceFormField(field: string): any {
        return this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.' + field);
    }

    /**
     * Set up the form builder.
     * @private
     */
    private setupFormGroup(): void {
        // Define our form builder.
        this.serviceForm = this.formBuilder.group({
            [WizardSubStepTitlesEnum.GENERAL]: this.formBuilder.group({
                id: [''], // <- Used outside of this component.
                serviceId: [null, Validators.required],
                conditionType: [null, Validators.required],
                condition: [null, Validators.required],
                conditionValues: new FormArray([new FormControl({ value: '', disabled: true }, null)]),
                conditionParam: [null, Validators.required],
                redirectType: [null, Validators.required],
                redirectLocation1: [{ value: null, disabled: this.backends.length === 1 }, Validators.required],
                redirectLocation2: [null, Validators.required],
                redirectCode: [null, Validators.required],
            })
        });

        this.setupClassVariablesFromForm(WizardTitleEnum.ACLS);
    }
}
